<script setup>
import { ref, onMounted, onUnmounted, defineProps, computed } from 'vue'
import { useTableStore } from '@/stores/tableStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePQAStore } from '@/stores/PQAStore.ts'
import Backend from '@/models/backend.ts'
import { useMapStore } from '@/stores/mapStore.ts'
import { usePlanetGroupStore } from '@/stores/planetGroupStore.ts'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores / models
 */
const tableStore = useTableStore()
const gameStore = useGameStore()
const pqaStore = usePQAStore()
const mapStore = useMapStore()
const planetGroupStore = usePlanetGroupStore()

/**
 * Props
 */
const props = defineProps({
  text: {
    type: String,
    default: 'Action'
  },
  smartPositioning: {
    type: Boolean,
    default: false
  }
})

/**
 * Variables
 */
const menuRef = ref(null)

/**
 * Functions
 */
function toggleMenu() {
  if (gameStore.pullDownMenuVisible === 'planetMultiSelectMenu') {
    closeMenu()
  } else {
    gameStore.pullDownMenuVisible = 'planetMultiSelectMenu'
  }
}

/**
 * Assign ships to a fleet
 */
const setProductionQueue = () => {
  if (tableStore.planets.selectedRowIds.length > 0) {
    gameStore.modalType = 'planetsSetProductionQueue'
    gameStore.modalData = tableStore.planets.selectedRowIds
    tableStore.planets.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

const setPopulationAssignments = () => {
  if (tableStore.planets.selectedRowIds.length > 0) {
    gameStore.modalType = 'planetsSetPopulationAssignments'
    gameStore.modalData = tableStore.planets.selectedRowIds
    tableStore.planets.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

const setTax = () => {
  if (tableStore.planets.selectedRowIds.length > 0) {
    gameStore.modalType = 'planetsSetTax'
    gameStore.modalData = tableStore.planets.selectedRowIds
    tableStore.planets.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

const clearProductionQueues = () => {
  const backend = new Backend()
  backend.clearProductionFromPlanet(tableStore.planets.selectedRowIds)
  gameStore.setTinySuccessToastMessage(t('common.menu.planets.productionCleared'))
  tableStore.planets.selectedAll = false

  // Disable area selection on the map
  mapStore.disableAreaSelection()

  closeMenu()
}

const buildShips = () => {
  if (tableStore.planets.selectedRowIds.length > 0) {
    gameStore.modalType = 'planetBuildShip'
    gameStore.modalData = { planetIds: tableStore.planets.selectedRowIds }
    tableStore.planets.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

const buildBuildings = () => {
  if (tableStore.planets.selectedRowIds.length > 0) {
    gameStore.modalType = 'planetBuildBuilding'
    gameStore.modalData = { planetIds: tableStore.planets.selectedRowIds }
    tableStore.planets.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

/**
 * Attach selected ships to a group
 */
const attachToGroup = () => {
  if (tableStore.planets.selectedRowIds.length > 0) {
    gameStore.modalType = 'attachToPlanetGroup'
    gameStore.modalData = tableStore.planets.selectedRowIds
    tableStore.planets.selectedRowIds = []
    tableStore.planets.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

const detachFromGroup = () => {
  if (tableStore.planets.selectedRowIds.length > 0) {
    const backend = new Backend()
    backend.detachPlanetsFromGroup(tableStore.planets.selectedRowIds)
    tableStore.planets.selectedRowIds = []
    tableStore.planets.selectedAll = false

    // Disable area selection on the map
    mapStore.disableAreaSelection()
  }
}

function closeMenu() {
  gameStore.pullDownMenuVisible = ''
}

function handleClickOutside(event) {
  if (!menuRef.value.contains(event.target)) {
    closeMenu()
  }
}

/**
 * Lifecycle hooks
 */
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Calculate dropdown position to keep it on screen
const dropdownPosition = computed(() => {
  if (!menuRef.value) return { top: '100%', left: '0' }

  // If smart positioning is disabled, always use default positioning
  if (!props.smartPositioning) {
    return { top: '100%', left: '0', transform: '' }
  }

  const menuWidth = 240 // w-60 = 240px
  const menuHeight = 320 // Approximate height based on menu items

  // Get map container dimensions
  const mapContainer = document.querySelector('.webgl-container')
  if (!mapContainer) return { top: '100%', left: '0' }

  const containerRect = mapContainer.getBoundingClientRect()
  const triggerRect = menuRef.value.parentElement?.getBoundingClientRect()

  if (!triggerRect) return { top: '100%', left: '0' }

  let top = '100%'
  let left = '0'
  let transform = ''

  // Check if dropdown would go off bottom of container
  if (triggerRect.bottom + menuHeight > containerRect.bottom) {
    // Flip to appear above, but still connected to the icon
    top = '0'
    transform = 'translateY(-100%)'
  }

  // Check if dropdown would go off right side of container
  if (triggerRect.left + menuWidth > containerRect.right) {
    // Align right edge of dropdown with right edge of trigger
    left = `-${menuWidth - triggerRect.width}px`
  }

  return { top, left, transform }
})
</script>

<template>
  <div class="relative" @click.stop="toggleMenu">
    <!-- Icon Button -->
    <slot></slot>
    <!-- Dropdown Menu -->
    <Transition name="menu">
      <div
        v-show="gameStore.pullDownMenuVisible === 'planetMultiSelectMenu'"
        ref="menuRef"
        class="absolute w-60 rounded-lg border border-metal-800 bg-metal-950 z-50 overflow-hidden"
        :style="{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          transform: dropdownPosition.transform
        }"
      >
        <!-- Header -->
        <div class="menu-header text-center text-metal-300 text-sm py-2 px-3">
          {{ t('common.menu.planets.selected', tableStore.planets.selectedRowIds.length) }}
        </div>

        <!-- Menu Items -->
        <div class="border-t border-metal-900">
          <div
            v-if="pqaStore.queues.size > 0"
            class="menu-item"
            @click="setProductionQueue"
          >
            <GenericIcon type="production" class="mr-3" />
            <span>{{ t('common.menu.planets.setProductionQueue') }}</span>
          </div>

          <div class="menu-item" @click="setPopulationAssignments">
            <GenericIcon type="person" class="mr-3" />
            <span>{{ t('common.menu.planets.setPopulationAssignments') }}</span>
          </div>

          <div class="menu-item" @click="setTax">
            <GenericIcon type="credit" class="mr-3" />
            <span>{{ t('common.menu.planets.setTax') }}</span>
          </div>

          <!-- Divider -->
          <div class="divider"></div>

          <div class="menu-item text-red-400" @click="clearProductionQueues">
            <GenericIcon type="trash" class="mr-3" />
            <span>{{ t('common.menu.planets.clearProduction') }}</span>
          </div>

          <div class="menu-item" @click="buildShips">
            <GenericIcon type="ship" class="mr-3" />
            <span>{{ t('common.menu.planets.buildShips') }}</span>
          </div>

          <div class="menu-item" @click="buildBuildings">
            <GenericIcon type="building" class="mr-3" />
            <span>{{ t('common.menu.planets.buildBuildings') }}</span>
          </div>

          <div v-if="planetGroupStore.planetGroups.size > 0">
            <!-- Divider -->
            <div class="divider"></div>

            <div class="menu-item" @click="attachToGroup">
              <GenericIcon type="add" class="mr-3" />
              <span>{{ t('common.menu.planets.addToGroup') }}</span>
            </div>

            <div class="menu-item" @click="detachFromGroup">
              <GenericIcon type="remove" class="mr-3" />
              <span>{{ t('common.menu.planets.removeFromGroup') }}</span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.menu-header {
  background: linear-gradient(to right, theme('colors.metal.800'), theme('colors.metal.900'));
}

.menu-item {
  @apply flex items-center p-2 text-metal-300 text-sm cursor-pointer;
  transition: background-color 150ms ease;

  &:hover {
    @apply bg-metal-800;
  }

  &:active {
    @apply bg-metal-700;
  }
}

.divider {
  @apply h-px mx-2 my-1 bg-metal-800;
}

/* Menu transition - matches modal style */
.menu-enter-active,
.menu-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
